import React, { useEffect, useState } from "react";

import ToastData, {type TooltipToastAppearance} from "../models/ToastData";
import { sleep } from "../models/utils";
import { isDevelopmentEnvironment } from "../estada-config";
import { isUiModeDeveloper } from "../uiMode";
import { EventKey } from "../eventLogger/EventKeys";

let autoHideTimer: ReturnType<typeof setTimeout> | null = null;
// All fire-and-forget close timers from non-keepVisible showToast calls.
// Tracked so a subsequent keepVisible showToast or hideToast can cancel them.
const closeTimers = new Set<ReturnType<typeof setTimeout>>();
let activeToastToken = 0;
let activeToastKind:
  | "message" // no itx
  | "tooltip" // no itx
  | undefined;

function cancelAllCloseTimers() {
  if (autoHideTimer !== null) {
    clearTimeout(autoHideTimer);
    autoHideTimer = null;
  }
  for (const t of closeTimers) clearTimeout(t);
  closeTimers.clear();
}

export function getCurrentToastToken() {
  return activeToastToken;
}

export function updateToastProgress(params: { progress: number; toastToken?: number }) {
  if (params.toastToken !== undefined && params.toastToken !== activeToastToken) return;

  const elm = document.getElementById("toastProgress") as HTMLProgressElement | null;
  if (elm) {
    elm.value = params.progress;

    if (params.progress == 100) {
      //
      // remove blured background fast for better user feedback
      //
      const elmWrapper = document.getElementById("toastDialogWrapper") as HTMLDivElement | null;
      elmWrapper?.classList.remove("dialogWrapper");

      if (autoHideTimer !== null) clearTimeout(autoHideTimer);
      autoHideTimer = setTimeout(() => {
        autoHideTimer = null;
        hideToast();
      }, 1000);
    }
  }
}

export function updateToastMessage(text: string, toastToken?: number) {
  if (toastToken !== undefined && toastToken !== activeToastToken) return;

  const elm = document.getElementById("toastMessageText");
  if (elm) {
    elm.textContent = text;
  }
}

export async function showToast(params: {
  isError?: boolean;
  title?: string;
  text: string;
  isSuccess?: boolean;
  keepVisible?: boolean;
  time?: number;
  modal?: boolean;
  progress?: boolean;
  isTooltip?: boolean;
  tooltipIconUrl?: string;
  tooltipAppearance?: TooltipToastAppearance;
}) {
  if (params.isTooltip && activeToastKind === "message") return;
  const { isError, title, text, isSuccess, time: givenTime, modal } = params;
  let { keepVisible } = params;
  if (keepVisible != true && state.programStat.operationInProgress) return;

  // Cancel ALL pending close timers so no stale timer from a prior toast
  // (e.g. a non-keepVisible toast during device export) can close this new one.
  cancelAllCloseTimers();
  activeToastToken++;
  const toastToken = activeToastToken;
  activeToastKind = params.isTooltip
    ? "tooltip" // no itx
    : "message"; // no itx

  const d = new ToastData();
  d.text = text?.replaceAll("\\n", "\n");
  d.tooltipIconUrl = params.isTooltip ? params.tooltipIconUrl : undefined;
  d.tooltipAppearance = params.isTooltip ? params.tooltipAppearance : undefined;
  d.title = title?.replaceAll("\\n", "\n");
  d.visible = true;
  d.progress = params.progress == true;
  // Fortschritts-Toasts verwenden den bereits vorhandenen modalen
  // Haupt-App-Wrapper mit Blur-Overlay. progress muss vor modal gesetzt sein.
  d.modal = modal === true || d.progress;

  if (isSuccess != undefined) {
    d.isSuccess = isSuccess;
  }
  if (isError != undefined) {
    d.isError = isError;
  }
  if (keepVisible != undefined) {
    d.visible = keepVisible;
  }

  if (d.progress == true) {
    d.visible = true; // always visible when progress is set
    keepVisible = true; // always keep visible when progress is set
  }

  let time = 5000;
  if (isError) {
    time = 10000;
  }
  if (givenTime) {
    time = givenTime;
  }

  const event = new CustomEvent("showToast", { detail: d });
  document.dispatchEvent(event);

  if (!d.modal && !d.progress && !params.isTooltip) {
    state.eventLogger.addEvent(EventKey.ToastShown, { text: d.text ?? "", isError: d.isError ?? false });
  }

  if (!keepVisible) {
    const t = setTimeout(() => {
      closeTimers.delete(t);
      if (toastToken !== activeToastToken) return;
      activeToastKind = undefined;
      const event = new CustomEvent("showToast", { detail: new ToastData() });
      document.dispatchEvent(event);
    }, time);
    closeTimers.add(t);
  }

  await sleep(10);
}

/** Hover hints share the toast surface without replacing application messages. */
export function showTooltipToast(text: string, iconUrl?: string, appearance?: TooltipToastAppearance): number | undefined {
  if (activeToastKind === "message") return undefined;
  const previous = activeToastToken;
  void showToast({text, tooltipIconUrl: iconUrl, tooltipAppearance: appearance, isTooltip: true, time: 5000});
  return activeToastToken !== previous ? activeToastToken : undefined;
}

export async function hideToast(toastToken?: number) {
  if (toastToken !== undefined && toastToken !== activeToastToken) return;

  // Cancel ALL pending close timers so none fires against a future toast
  cancelAllCloseTimers();
  activeToastToken++;
  activeToastKind = undefined;
  const event = new CustomEvent("showToast", { detail: new ToastData() });
  document.dispatchEvent(event);
  await sleep(10);
}

export default function Toasts() {
  const [toastData, setToastData] = useState<ToastData>(new ToastData());

  function handleShowToast(ev: any) {
    if (ev.progress != undefined) {
      setToastData({ ...toastData, progress: ev.progress });
    } else {
      setToastData(ev.detail);
    }
  }

  useEffect(() => {
    document.addEventListener("showToast", handleShowToast);
    return () => {
      document.removeEventListener("showToast", handleShowToast);
    };
  }, []);

  const text = toastData?.text;
  if (!text) {
    return null;
  }

  const txt: any = [];
  const ss = text.split("\n");

  for (let i = 0; i < ss.length; i++) {
    txt.push(ss[i]);
    if (i < ss.length - 1) {
      txt.push(<br key={"beak" + ss[i] + i} />);
    }
  }

  const isError = toastData?.isError || false;

  console.log("render Toast");

  return (
    <div
      id="toastDialogWrapper"
      className={toastData.modal ? "dialogWrapper toastDialogWrapper" : ""}
    >
      <div
        id="toast"
        className="App Toast hideOnPrinter"
        style={{
          position: "fixed",
          display: "flex",
          flexDirection: "column",
          bottom: "50px",
          right: "50px",
          width: "max-content",
          maxWidth: "75vw",
          visibility: toastData.visible ? "visible" : "hidden",
          opacity: "1",
          color: toastData.tooltipAppearance?.color,
          backgroundColor: toastData.tooltipAppearance?.backgroundColor ?? (isError
            ? "var(--error-color)"
            : toastData.isSuccess
              ? "var(--knxColorGreen)" // no itx
              : "var(--primary-color)"), // no itx
          padding: "10px",
          paddingLeft: "20px",
          paddingRight: "20px",
          borderRadius: "20px",
          boxShadow: "var(--panel-shadow)",
          transition: "all 1s ease-out 0s",
          zIndex: "5000",
          height: "max-content",
          whiteSpace: "pre",
        }}
      >
        {toastData.title && <div style={{ fontWeight: "bold", whiteSpace: "pre" }}>{toastData.title}</div>}
        <div id="toastMessageText">{toastData.tooltipIconUrl ? <>
          <span style={{display: "inline-flex", alignItems: "center", gap: "6px"}}>
            <img src={toastData.tooltipIconUrl} alt="" style={{width: "1.5em", height: "1.5em", objectFit: "contain"}} />
            {ss[0]}
          </span>
          {ss.slice(1).map((line, index) => <div key={index}>{line}</div>)}
        </> : txt}</div>

        {toastData.progress && (
          <div style={{ marginTop: "10px" }}>
            <progress id="toastProgress" value={0} max="100" />
          </div>
        )}
      </div>
    </div>
  );
}

export function showErrToast(errMsg: string, errTitle?: string) {
  showToast({
    title: errTitle ? errTitle : "",
    isError: true,
    text: errMsg,
  });
}

export function showDebugErrToast(errMsg: string) {
  if (!isDevelopmentEnvironment() && !isUiModeDeveloper()) {
    return;
  }
  showToast({
    isError: true,
    text: errMsg,
  });
}
